// Enums
export type ExperienceLevel = 'first_time' | 'some' | 'veteran';
export type BudgetRange = 'under_500' | '500_800' | '800_1200' | '1200_plus' | 'unlimited';
export type CostumePreference = 'frontline' | 'midline' | 'backline' | 'no_preference';
export type VibeTag = 'bacchanal' | 'pretty' | 'music' | 'social' | 'cultural';
export type MusicTag = 'soca' | 'dancehall' | 'afrobeats' | 'bouyon' | 'everything';
export type CrowdPreference = 'young' | 'mixed' | 'mature' | 'any';
export type AgeRange = '18_24' | '25_34' | '35_44' | '45_plus';
export type SwipeAction = 'pass' | 'match' | 'connect';
export type ManagerRole = 'owner' | 'admin' | 'viewer';

// Location options (country/region)
export const LOCATIONS = [
  'Trinidad & Tobago',
  'USA - New York',
  'USA - Florida',
  'USA - California',
  'USA - Texas',
  'USA - Other',
  'Canada - Toronto',
  'Canada - Other',
  'UK - London',
  'UK - Other',
  'Jamaica',
  'Barbados',
  'Other Caribbean',
  'Other',
] as const;

export type Location = typeof LOCATIONS[number];

// Trinidad areas - where user will be coming from on Carnival day (for logistics)
export const TRINIDAD_AREAS = [
  'North West (Port of Spain, Diego Martin, Westmoorings)',
  'North East (Arima, Sangre Grande, Toco)',
  'Central (Chaguanas, Couva, Cunupia)',
  'South West (San Fernando, Point Fortin, La Brea)',
  'South East (Princes Town, Mayaro, Rio Claro)',
  'Tobago',
  'Not sure yet',
] as const;

export type TrinidadArea = typeof TRINIDAD_AREAS[number];

// Past bands list
export const PAST_BANDS = [
  'Tribe',
  'YUMA',
  'Lost Tribe',
  'Bliss',
  'Fantasy',
  'Harts',
  'Spice',
  'Passion',
  'Ronnie & Caro',
  'D Krewe',
  'Other',
  'None - First time',
] as const;

export interface User {
  id: string;
  email: string;
  created_at: string;

  // Basic info (Step 2)
  display_name: string | null;
  location: Location | null; // Where user lives
  trinidad_area: TrinidadArea | null; // Where user will come from on Carnival day (logistics)
  age_range: AgeRange | null;

  // Experience (Step 3)
  experience_level: ExperienceLevel | null;
  past_bands: string[];

  // Budget (Step 4)
  budget_range: BudgetRange | null;
  costume_preference: CostumePreference | null;

  // Preferences (Steps 5-7)
  vibe_preferences: VibeTag[];
  music_preferences: MusicTag[];
  crowd_preference: CrowdPreference | null;

  // Matching weights (auto-adjusted)
  weight_budget: number;
  weight_vibe: number;
  weight_music: number;
  weight_crowd: number;
  weight_friends: number;

  // System
  app_code: string; // Unique 6-char code for friend invites
  onboarding_completed: boolean;
  last_active: string | null;
}

export interface Band {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  website_url: string | null;
  instagram_url: string | null;
  logo_url: string | null;
  hero_image_url: string | null;
  price_min: number | null;
  price_max: number | null;
  vibe_tags: VibeTag[];
  music_tags: MusicTag[];
  crowd_type: CrowdPreference | null;
  section_count: number | null;
  inclusives: string[];
  distribution_points: string[];
  route_description: string | null;
  is_active: boolean;
  carnival_year: number;
  created_at: string;
}

export interface BandImage {
  id: string;
  band_id: string;
  image_url: string;
  display_order: number;
  is_costume: boolean;
  section_name: string | null;
  created_at: string;
}

export interface Swipe {
  id: string;
  user_id: string;
  band_id: string;
  action: SwipeAction;
  time_spent_seconds: number | null;
  expanded_profile: boolean;
  created_at: string;
  carnival_year: number;
}

export interface Friendship {
  id: string;
  user_id: string;
  friend_id: string;
  created_at: string;
  // Note: Mutual friendship = both directions exist in the table
  // A added B: friendship(A, B) exists
  // B added A: friendship(B, A) exists
  // Mutual: both exist
}

export interface BandManager {
  id: string;
  user_id: string;
  band_id: string;
  role: ManagerRole;
  created_at: string;
}

export interface AnalyticsEvent {
  id: string;
  band_id: string;
  user_id: string | null;
  event_type: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  carnival_year: number;
}
